import React, { useEffect, useState } from "react";
import { Box, Spinner } from "theme-ui";
import { useNavigate } from "react-router-dom";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Subtext from "components/typography/Subtext";
import { mnt, mntCard, mntType } from "theme/maintenanceTokens";

import { getServicePlans } from "api/ServicePlans";

/**
 * CLIENT view of /maintenance (Session 62) — a lean, read-only list of
 * the client's own maintenance plan(s). Rendered on the SAME route as the
 * operator MaintenanceIndex and gated by role in that component; the
 * operator cross-client console (WO triage summary, per-plan progress,
 * create-plan) is never mounted for a client.
 *
 * The backend `GET /maintenance/plans` is server-scoped to the client's
 * own org (parent-aware) and strips pricing for non-company requesters,
 * so this trusts the scoping and renders what comes back. Most clients
 * have one plan; the row links into the plan detail (also role-branched).
 */
export default function MaintenanceClientIndex() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Maintenance";
    let alive = true;
    getServicePlans({ query: {} })
      .then((rows: any) => {
        if (!alive) return;
        const list = Array.isArray(rows) ? rows : rows?.items ?? [];
        setPlans(list);
        setLoading(false);
      })
      .catch(() => {
        if (alive) {
          setPlans([]);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <>
        <Header></Header>
        <Page title="Maintenance">
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  const rows = plans || [];

  return (
    <>
      <Header></Header>
      <Page title="Maintenance" $noPadding={true}>
        <Box sx={{ maxWidth: "1100px", marginX: "auto", width: "100%" }}>
          <Box sx={{ marginTop: "16px", marginBottom: "18px" }}>
            <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"20px"}>
              Your maintenance {rows.length === 1 ? "plan" : "plans"}
            </H5>
            <Subtext fontSize={"13px"} fontFamily={"Poppins"} marginTop={"6px"} marginBottom={0} color={mnt.textMuted}>
              {rows.length === 0
                ? "No maintenance plan is associated with your account yet."
                : `${rows.length} plan${rows.length === 1 ? "" : "s"}`}
            </Subtext>
          </Box>

          {rows.length === 0 ? (
            <Box sx={{ ...mntCard, padding: "24px", textAlign: "center" }}>
              <P fontSize={"14px"} color={mnt.textMuted} margin={0}>
                There is no maintenance plan on your account yet. Your GetMulch account team sets
                these up.
              </P>
            </Box>
          ) : (
            <Box sx={{ ...mntCard, padding: "6px 0" }}>
              {rows.map((plan: any) => {
                const term =
                  plan?.startDate || plan?.endDate
                    ? [fmtDate(plan.startDate), fmtDate(plan.endDate)].filter(Boolean).join(" - ")
                    : "Ongoing";
                return (
                  <Box
                    as="button"
                    key={plan.id}
                    onClick={() => navigate(`/maintenance/plans/${plan.id}`)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      textAlign: "left",
                      background: "transparent",
                      border: "none",
                      borderBottom: `1px solid ${mnt.divider}`,
                      padding: "14px 16px",
                      cursor: "pointer",
                      "&:last-of-type": { borderBottom: "none" },
                      "&:hover": { background: mnt.bgSubtle },
                    }}
                  >
                    <Box>
                      <Box sx={{ ...mntType.labelSm, color: mnt.ink, fontWeight: 600, fontSize: "15px" }}>
                        {plan.name || "Maintenance plan"}
                      </Box>
                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: mnt.textMuted, marginTop: "3px" }}>
                        {term}
                      </Box>
                    </Box>
                    <Box
                      sx={{ fontFamily: "Poppins", fontSize: "18px", color: mnt.textFaint, paddingLeft: "12px", flexShrink: 0 }}
                      aria-hidden="true"
                    >
                      {"\u203A"}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Page>
    </>
  );
}

function fmtDate(d: any): string {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
